"use client";

import * as React from "react";
import { cn } from "@/lib/utils";

interface JourneyTimelineProps {
  className?: string;
  stageCount: number;
}

export const JourneyTimeline = ({
  className,
  stageCount,
  ...props
}: JourneyTimelineProps) => {
  const [progress, setProgress] = React.useState(0);
  const containerRef = React.useRef<HTMLDivElement>(null);

  const calculateMaxLineHeight = (stages: number) =>
    stages > 0 ? ((stages - 1) / stages) * 100 : 0;

  const calculateFillHeight = (progressValue: number, maxHeight: number) =>
    Math.min((progressValue / 100) * maxHeight, maxHeight);

  const maxLineHeightPercentage = calculateMaxLineHeight(stageCount);
  const maxLineHeight = `${maxLineHeightPercentage}%`;
  const fillHeightPercentage = calculateFillHeight(
    progress,
    maxLineHeightPercentage,
  );
  const fillHeight = `${fillHeightPercentage}%`;

  React.useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return;

      const container = containerRef.current.parentElement;
      if (!container) return;

      const rect = container.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      const startOffset = viewportHeight * 0.7;
      const endOffset = viewportHeight * 0.3;

      const sectionTop = rect.top;
      const sectionBottom = rect.bottom;
      const sectionHeight = rect.height;

      if (sectionTop > startOffset) {
        setProgress(0);
        return;
      }

      if (sectionBottom < endOffset) {
        setProgress(100);
        return;
      }

      const scrollableDistance = sectionHeight - (startOffset - endOffset);
      const scrolled = startOffset - sectionTop;
      const progressValue = Math.max(
        0,
        Math.min(100, (scrolled / scrollableDistance) * 100),
      );

      setProgress(progressValue);
    };

    handleScroll();

    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className={cn("pointer-events-none absolute inset-0", className)}
      {...props}
    >
      <div
        id="timeline-background"
        className={cn(
          "absolute top-0 w-0.5 bg-gray-300",
          "right-4 lg:right-auto lg:left-1/2 lg:-translate-x-1/2",
        )}
        style={{ height: maxLineHeight }}
      />

      <div
        id="timeline-fill"
        className={cn(
          "bg-primary absolute top-0 w-0.5",
          "right-4 lg:right-auto lg:left-1/2 lg:-translate-x-1/2",
        )}
        style={{
          height: fillHeight,
          transition: "height 150ms ease-out",
        }}
      />

      <div id="timeline-dots">
        {Array.from({ length: stageCount }, (_, index) => {
          const position = index / stageCount;
          const topPosition = `${position * 100}%`;
          const dotThreshold = (index / (stageCount - 1)) * 100;
          const isFilled = progress >= dotThreshold;

          return (
            <div
              key={index}
              id={`timeline-dot-${index}`}
              className={cn(
                "absolute h-4 w-4 -translate-y-1/2 rounded-full",
                "right-4 translate-x-1/2 lg:right-auto lg:left-1/2 lg:-translate-x-1/2",
                isFilled ? "bg-primary" : "bg-gray-300",
              )}
              style={{
                top: topPosition,
                transition: "background-color 300ms ease-out",
              }}
              data-threshold={dotThreshold}
            />
          );
        })}
      </div>
    </div>
  );
};
